import { z } from 'zod';

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const CreatePlanSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  maxEmployees: z.number().int().positive().optional(),
  maxModules: z.number().int().positive().optional(),
  priceMonthly: z.number().min(0),
  priceYearly: z.number().min(0),
  currency: z.string().default('INR'),
  features: z.record(z.unknown()).optional(),
});
export type CreatePlanInput = z.infer<typeof CreatePlanSchema>;

export const UpdatePlanSchema = CreatePlanSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdatePlanInput = z.infer<typeof UpdatePlanSchema>;

export const PlanSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  maxEmployees: z.number().nullable(),
  maxModules: z.number().nullable(),
  priceMonthly: z.string(),
  priceYearly: z.string(),
  currency: z.string(),
  isActive: z.boolean(),
  features: z.unknown().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Plan = z.infer<typeof PlanSchema>;

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export const AssignPlanSchema = z.object({
  planId: z.string().uuid(),
  billingCycle: z.enum(['monthly', 'yearly']).default('monthly'),
  currentPeriodStart: z.string(),
  currentPeriodEnd: z.string(),
  trialEndsAt: z.string().optional(),
  status: z.enum(['trial', 'active', 'past_due', 'cancelled', 'suspended']).default('active'),
});
export type AssignPlanInput = z.infer<typeof AssignPlanSchema>;

export const SetDiscountSchema = z.object({
  discountPercent: z.number().min(0).max(100).default(0),
  discountNote: z.string().optional(),
  customPriceMonthly: z.number().min(0).nullable().optional(),
  customPriceYearly: z.number().min(0).nullable().optional(),
});
export type SetDiscountInput = z.infer<typeof SetDiscountSchema>;

export const SetSubscriptionStatusSchema = z.object({
  status: z.enum(['trial', 'active', 'past_due', 'cancelled', 'suspended']),
});
export type SetSubscriptionStatusInput = z.infer<typeof SetSubscriptionStatusSchema>;

export const AdminSubscriptionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  tenantSlug: z.string(),
  planId: z.string().uuid(),
  planName: z.string(),
  status: z.string(),
  billingCycle: z.string(),
  currentPeriodStart: z.string(),
  currentPeriodEnd: z.string(),
  trialEndsAt: z.string().nullable(),
  discountPercent: z.string(),
  discountNote: z.string().nullable(),
  customPriceMonthly: z.string().nullable(),
  customPriceYearly: z.string().nullable(),
  effectiveMonthly: z.string(),
  createdAt: z.string(),
});
export type AdminSubscription = z.infer<typeof AdminSubscriptionSchema>;

// ---------------------------------------------------------------------------
// Module activations
// ---------------------------------------------------------------------------

export const ModuleToggleSchema = z.object({
  moduleKey: z.string(),
  isEnabled: z.boolean(),
});
export type ModuleToggle = z.infer<typeof ModuleToggleSchema>;

export const BulkSetModulesSchema = z.object({
  modules: z.array(ModuleToggleSchema),
});
export type BulkSetModulesInput = z.infer<typeof BulkSetModulesSchema>;

export const AdminModuleActivationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  moduleKey: z.string(),
  isEnabled: z.boolean(),
  updatedAt: z.string(),
});
export type AdminModuleActivation = z.infer<typeof AdminModuleActivationSchema>;

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export const AdminCreateInvoiceSchema = z.object({
  amount: z.number().min(0),
  currency: z.string().default('INR'),
  dueDate: z.string(),
  invoiceNumber: z.string().min(1),
  lineItems: z.array(z.record(z.unknown())).optional(),
});
export type AdminCreateInvoiceInput = z.infer<typeof AdminCreateInvoiceSchema>;

export const AdminPatchInvoiceStatusSchema = z.object({
  status: z.enum(['draft', 'sent', 'paid', 'overdue', 'void']),
  paymentReference: z.string().optional(),
});
export type AdminPatchInvoiceStatusInput = z.infer<typeof AdminPatchInvoiceStatusSchema>;

export const AdminInvoiceSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantName: z.string(),
  invoiceNumber: z.string(),
  amount: z.string(),
  currency: z.string(),
  status: z.string(),
  dueDate: z.string(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminInvoice = z.infer<typeof AdminInvoiceSchema>;
